import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import config from '../config';
import {
  msalInstance,
  authConfigured,
  ensureMsalReady,
  acquireApiToken,
  clearStaleAccount,
  loginRequest,
} from '../auth/msal';

const AuthContext = createContext(null);

/**
 * Normalize an MSAL AccountInfo into the small shape the UI consumes.
 */
function toUser(account) {
  if (!account) return null;
  return {
    name: account.name || account.username || 'User',
    email: account.username || '',
    // Preserve the raw MSAL fields so consumers relying on them (e.g. TopBar's
    // claims-based display-name fallback) keep working.
    username: account.username || '',
    idTokenClaims: account.idTokenClaims || {},
    oid: account.localAccountId || account.homeAccountId || '',
    // Entra tenant (directory) ID. MSAL exposes it on the account and also as
    // the `tid` claim on the ID token; prefer the account field.
    tenantId: account.tenantId || account.idTokenClaims?.tid || '',
    // App roles arrive as a claim on the ID token; expose for role-gated UI.
    roles: account.idTokenClaims?.roles || [],
    account,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Distinct from isLoading: true only while we're bouncing to AAD, so the
  // UI can show "Redirecting to sign-in…" instead of a generic spinner.
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Guards against firing loginRedirect twice (React 18 StrictMode invokes
  // effects twice in dev; a second loginRedirect throws interaction_in_progress).
  const redirectingRef = useRef(false);

  const login = useCallback(async () => {
    if (!msalInstance) return;
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    setIsRedirecting(true);
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      if (err?.errorCode !== 'interaction_in_progress') {
        console.error('Login redirect failed', err);
        redirectingRef.current = false;
        setIsRedirecting(false);
      }
    }
  }, []);

  const logout = useCallback(async () => {
    if (!msalInstance) return;
    const account = msalInstance.getActiveAccount() || undefined;
    // Full-page redirect sign-out clears the MSAL cache AND the AAD session,
    // then returns the user to postLogoutRedirectUri.
    await msalInstance.logoutRedirect({
      account,
      postLogoutRedirectUri: config.auth.postLogoutRedirectUri,
    });
  }, []);

  const getToken = useCallback(async () => acquireApiToken(), []);

  // Case-insensitive role check. AAD app-role names are typically PascalCase
  // ("SystemAdmin") but callers may pass any casing, so normalize both sides.
  const hasRole = useCallback(
    (role) => {
      if (!role) return false;
      const wanted = String(role).toLowerCase();
      return (user?.roles || []).some((r) => String(r).toLowerCase() === wanted);
    },
    [user]
  );

  // Convenience flag for the app-wide admin role (Settings tab, etc.).
  const isAdmin = hasRole(config.rbac.adminRole);

  useEffect(() => {
    let cancelled = false;

    // ── Local dev / auth-not-configured mode ────────────────────────────
    // No client id → don't attempt AAD; render as a placeholder user so the
    // app is usable locally (backend also bypasses auth via SKIP_AUTH).
    if (!authConfigured) {
      const devName =
        process.env.REACT_APP_DEV_USER_NAME || process.env.REACT_APP_USER || '';
      const devEmail = process.env.REACT_APP_DEV_USER_EMAIL || '';
      setUser({
        name: devName || (devEmail ? devEmail.split('@')[0] : 'Local user'),
        email: devEmail || 'dev@localhost',
        roles: ['Admin', config.rbac.adminRole, config.rbac.modelTestRole],
      });
      setIsAuthenticated(true);
      setIsLoading(false);
      return () => {};
    }

    (async () => {
      try {
        // Completes any in-progress redirect and returns the active account.
        const account = await ensureMsalReady();
        if (cancelled) return;

        if (account) {
          // IMPORTANT: a cached ACCOUNT is not proof of a live SESSION.
          // MSAL persists accounts in localStorage, so getActiveAccount()
          // keeps returning the previous user long after their refresh token
          // has expired or been revoked. Trusting it here renders the app
          // (name, avatar, dashboard) for a split second before the first API
          // call fails and bounces the user to sign-in.
          //
          // So we validate the session by acquiring a token silently. This is
          // cheap (served straight from cache when still valid) and is the
          // only reliable way to know the session is genuinely usable.
          // interactiveFallback:false keeps this from hijacking the page —
          // the auto-login effect below owns that decision.
          const token = await acquireApiToken({ interactiveFallback: false });
          if (cancelled) return;

          if (token) {
            setUser(toUser(account));
            setIsAuthenticated(true);
          } else {
            // Stale account with no usable token: drop it so the UI shows
            // nothing personal and we cleanly fall through to sign-in.
            await clearStaleAccount(account);
            if (cancelled) return;
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Auth initialization failed', err);
        if (!cancelled) {
          setUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-login: once init has finished and we're not signed in, redirect to
  // Azure AD. Kept in a SEPARATE effect (reacting to state) so that React 18
  // StrictMode's double-invoke of the init effect can't skip or race it.
  useEffect(() => {
    if (!authConfigured || !config.auth.autoLogin) return;
    if (isLoading || isAuthenticated) return;
    if (redirectingRef.current) return;

    redirectingRef.current = true;
    setIsRedirecting(true);
    msalInstance.loginRedirect(loginRequest).catch((err) => {
      // interaction_in_progress just means a redirect is already underway.
      if (err?.errorCode !== 'interaction_in_progress') {
        console.error('Auto-login redirect failed', err);
      }
    });
  }, [isLoading, isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isRedirecting,
        authConfigured,
        login,
        logout,
        getToken,
        hasRole,
        isAdmin,
        msalInstance,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
